import { type RefObject, useEffect } from 'react';

const DEFAULT_ROOT_MARGIN = '100px 0px';

/**
 * Observes the `[data-page-index]` children of `containerRef` and calls `onPageVisible` once per
 * page, the first time that page scrolls within `rootMargin` of the viewport. A page stops being
 * observed as soon as it has fired once, since callers use this to trigger one-shot work (like
 * requesting that page's render) rather than to track ongoing visibility.
 */
// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export function usePageVisibility(
    containerRef: RefObject<HTMLElement | null>,
    pageCount: number,
    onPageVisible: (pageIndex: number) => void,
    rootMargin: string = DEFAULT_ROOT_MARGIN,
): void {
    useEffect(() => {
        const root = containerRef.current;
        if (!root || pageCount === 0) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting) {
                        continue;
                    }
                    observer.unobserve(entry.target);
                    const pageIndex = Number((entry.target as HTMLElement).dataset.pageIndex);
                    onPageVisible(pageIndex);
                }
            },
            { rootMargin, threshold: 0.01 },
        );
        for (const element of root.querySelectorAll('[data-page-index]')) {
            observer.observe(element);
        }
        return () => observer.disconnect();
    }, [containerRef, pageCount, onPageVisible, rootMargin]);
}
